import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, MapPin, Sparkles } from "lucide-react";
import { useLang } from "@/i18n";

const lineVariants = {
  hidden: { y: "115%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 1.1, ease: [0.22, 1, 0.36, 1], delay: 0.35 + i * 0.14 },
  }),
};

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (d) => ({ opacity: 1, y: 0, transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: d } }),
};

export const Hero = () => {
  const ref = useRef(null);
  const { t } = useLang();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const bgY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section ref={ref} className="relative min-h-screen flex items-center overflow-hidden pt-28 pb-16" data-testid="hero-section">
      <motion.div style={{ y: bgY }} className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <div className="absolute -top-32 -right-32 w-[38rem] h-[38rem] rounded-full bg-[radial-gradient(circle_at_center,#BAE6FD_0%,transparent_65%)] opacity-60" />
        <div className="absolute top-1/2 -left-48 w-[30rem] h-[30rem] rounded-full bg-[radial-gradient(circle_at_center,#A5F3FC_0%,transparent_65%)] opacity-50" />
        <span className="watermark absolute -bottom-10 left-0 text-[19vw] text-[#E3F2F9]">ADRIATICO</span>
      </motion.div>

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-8 items-center w-full">
        <div className="lg:col-span-7">
          <motion.div custom={0.15} variants={fadeUp} initial="hidden" animate="visible"
            className="inline-flex items-center gap-2 rounded-full border border-[#BAE6FD] bg-white/70 px-4 py-2 mb-8"
            data-testid="hero-status-pill">
            <Sparkles size={14} className="text-[#06B6D4]" />
            <span className="font-mono-ed text-[11px] sm:text-xs uppercase tracking-[0.18em] text-[#0369A1]">
              {t.hero.pill}
            </span>
          </motion.div>

          <h1 className="font-display font-extrabold tracking-tight leading-[1.02] text-[13.5vw] sm:text-7xl lg:text-[5.4rem] text-[#0B2530]">
            <span className="hero-mask"><motion.span custom={0} variants={lineVariants} initial="hidden" animate="visible" className="block">{t.hero.l1}</motion.span></span>
            <span className="hero-mask"><motion.span custom={1} variants={lineVariants} initial="hidden" animate="visible" className="block">{t.hero.l2a}<em className="font-serif-ed italic font-medium text-[#0284C7]">{t.hero.l2em}</em>{t.hero.l2b}</motion.span></span>
            <span className="hero-mask"><motion.span custom={2} variants={lineVariants} initial="hidden" animate="visible" className="block">{t.hero.l3a}<em className="font-serif-ed italic font-medium text-[#E8894B]">{t.hero.l3em}</em></motion.span></span>
          </h1>

          <motion.p custom={1.15} variants={fadeUp} initial="hidden" animate="visible"
            className="mt-8 max-w-xl text-base sm:text-lg text-[#3D5A66] leading-relaxed" data-testid="hero-subtitle">
            {t.hero.sub}
          </motion.p>

          <motion.div custom={1.35} variants={fadeUp} initial="hidden" animate="visible" className="mt-10 flex flex-wrap items-center gap-4">
            <a href="#cronostoria" data-testid="hero-cta-storia"
              className="group inline-flex items-center gap-2 rounded-full bg-[#0284C7] text-white font-semibold px-7 py-3.5 hover:bg-[#075E8A] transition-colors duration-300 active:scale-95 shadow-lg shadow-sky-500/25">
              {t.hero.cta1}
              <ArrowDown size={16} className="transition-transform duration-300 group-hover:translate-y-0.5" />
            </a>
            <Link to="/galleria" data-testid="hero-cta-galleria"
              className="inline-flex items-center gap-2 rounded-full border-2 border-[#0B2530]/15 bg-white/60 text-[#0B2530] font-semibold px-7 py-3.5 hover:border-[#06B6D4] hover:text-[#0284C7] transition-colors duration-300 active:scale-95">
              {t.hero.cta2}
            </Link>
          </motion.div>

          <motion.div custom={1.55} variants={fadeUp} initial="hidden" animate="visible"
            className="mt-14 grid grid-cols-3 gap-6 max-w-lg" data-testid="hero-stats">
            {t.hero.stats.map(([n, l]) => (
              <div key={l} className="border-l-2 border-[#A5F3FC] pl-4">
                <div className="font-display text-2xl sm:text-3xl font-extrabold text-[#0284C7]">{n}</div>
                <div className="text-xs sm:text-sm text-[#5F7E8A] leading-snug mt-1">{l}</div>
              </div>
            ))}
          </motion.div>
        </div>

        <div className="lg:col-span-5 relative">
          <motion.div
            initial={{ opacity: 0, y: 60, rotate: 3 }}
            animate={{ opacity: 1, y: 0, rotate: 0 }}
            transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1], delay: 0.6 }}
            className="relative mx-auto max-w-sm lg:max-w-none"
          >
            <div className="absolute -inset-3 rounded-[2.2rem] border border-[#BAE6FD]" aria-hidden="true" />
            <motion.div style={{ y: imgY }} className="relative rounded-[2rem] overflow-hidden img-frame">
              <img src="/images/hero-yuri.jpg" alt="Yuri Selvaggio" className="w-full h-[30rem] sm:h-[34rem] object-cover object-top" data-testid="hero-portrait" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#075E8A]/30 via-transparent to-transparent" />
            </motion.div>
            <div className="absolute -bottom-6 -left-6 glass-nav rounded-2xl border border-[#DCEEF5] px-5 py-4 float-slow shadow-xl shadow-sky-500/10" data-testid="hero-badge">
              <div className="flex items-center gap-2 text-[#0284C7]">
                <MapPin size={15} />
                <span className="font-mono-ed text-[11px] uppercase tracking-[0.16em]">{t.hero.badgePlace}</span>
              </div>
              <div className="font-serif-ed italic text-lg text-[#0B2530] mt-1">{t.hero.badgeQuote}</div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
